import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';

import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import StatsStrip from '../components/StatsStrip';
import CategoriesSection from '../components/CategoriesSection';
import ListingsSection from '../components/ListingsSection';
import Footer from '../components/Footer';
import PrivacyConsentBanner from '../components/PrivacyConsentBanner';
import Modal from '../components/Modal';
import AuthModal from '../components/AuthModal';
import AuthCTA from '../components/AuthCTA';

import type { Tab } from '../types';
import { useSavedItems } from '../hooks/useSavedItems';
import { useMobileMenu } from '../hooks/useMobileMenu';

interface ConsentState {
  essential: true;
  analytics: boolean;
  personalizedAds: boolean;
}

const PRIVACY_CONSENT_KEY = 'adsworld_privacy_consent';

export default function HomePage(): JSX.Element {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<Tab>('All');
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState<'signin' | 'signup'>('signin');

  const { savedItems, toggleSave } = useSavedItems();
  const { mobileOpen, toggleMobile } = useMobileMenu();


  // Check for auth redirect from verification
  useEffect(() => {
    const state = location.state as { openAuth?: boolean; authView?: 'signin' | 'signup' };
    if (state?.openAuth) {
      setAuthView(state.authView || 'signin');
      setShowAuthModal(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const stored = localStorage.getItem(PRIVACY_CONSENT_KEY);
    if (!stored) {
      setShowPrivacyBanner(true);
      return;
    }

    try {
      const parsed = JSON.parse(stored) as ConsentState;
      if (
        parsed &&
        typeof parsed.analytics === 'boolean' &&
        typeof parsed.personalizedAds === 'boolean'
      ) {
        return;
      }
      setShowPrivacyBanner(true);
    } catch {
      setShowPrivacyBanner(true);
    }
  }, []);

  const saveConsent = (nextConsent: ConsentState): void => {
    localStorage.setItem(PRIVACY_CONSENT_KEY, JSON.stringify(nextConsent));
    setShowPrivacyBanner(false);
  };

  const handleAcceptAll = (): void => {
    saveConsent({ essential: true, analytics: true, personalizedAds: true });
  };

  const handleRejectNonEssential = (): void => {
    saveConsent({ essential: true, analytics: false, personalizedAds: false });
  };

  const handleSaveCustomConsent = (options: { analytics: boolean; personalizedAds: boolean }): void => {
    saveConsent({ essential: true, analytics: options.analytics, personalizedAds: options.personalizedAds });
  };

  const openPrivacySettings = (): void => {
    setShowPrivacyBanner(true);
  };

  // Schema.org structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'BazaarPK',
    url: 'https://adsworld24.com',
    description: "Buy and sell cars, property, mobiles, jobs, and more in Pakistan. A simple marketplace connecting buyers and sellers.",
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://adsworld24.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'BazaarPK',
    url: 'https://adsworld24.com',
    logo: 'https://adsworld24.com/adsworld.png',
    sameAs: [
      'https://facebook.com/bazaarpk',
      'https://twitter.com/bazaarpk',
      'https://instagram.com/bazaarpk',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+92-302-4333398',
      contactType: 'customer service',
      areaServed: 'PK',
      availableLanguage: ['English', 'Urdu'],
    },
  };

  return (
    <>
      {/* ── React Helmet SEO ── */}
      <Helmet>
        <html lang="en" />
        <title>AdsWorld - Buy & Sell in Pakistan | Cars, Property, Mobiles, Jobs</title>
        <meta
          name="description"
          content="Buy and sell cars, property, mobiles, jobs, bikes and more in Pakistan. Connect with local buyers and sellers through our simple marketplace."
        />
        <meta
          name="keywords"
          content="AdsWorld, buy sell Pakistan, classifieds Pakistan, cars, property, jobs, mobiles, online marketplace"
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://adsworld24.com" />
        
        {/* Preconnect to external domains */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BazaarPK" />
        <meta property="og:title" content="AdsWorld - Buy & Sell in Pakistan" />
        <meta property="og:description" content="Connect with local buyers and sellers. Find cars, property, mobiles, jobs, and more." />
        <meta property="og:url" content="https://adsworld24.com" />
        <meta property="og:image" content="https://adsworld24.com/adsworld.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_PK" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bazaarpk" />
        <meta name="twitter:title" content="AdsWorld - Buy & Sell in Pakistan" />
        <meta name="twitter:description" content="Connect with local buyers and sellers. Find cars, property, mobiles, jobs, and more." />
        <meta name="twitter:image" content="https://adsworld24.com/adsworld.png" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-surface">
        {/* Skip to main content - Accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 bg-dark-900 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          Skip to main content
        </a>

        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <Hero searchQuery={searchQuery} onSearchChange={setSearchQuery} />

        <StatsStrip />

        <main
          id="main-content"
          className="main-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16"
        >
          <AuthCTA/>

          <CategoriesSection />

          <ListingsSection
            activeTab={activeTab}
            onTabChange={setActiveTab}
            savedItems={savedItems}
            onToggleSave={toggleSave}
          />
        </main>

        <Footer onOpenPrivacySettings={openPrivacySettings} />

        {/* WhatsApp floating button */}
        <button
          className="wa-float"
          aria-label="Chat with support on WhatsApp"
          title="Chat on WhatsApp"
        >
          <MessageCircle className="w-6 h-6" />
        </button>

        {showPrivacyBanner && (
          <>
            <div className="fixed inset-0 z-[60] bg-dark-900/25 backdrop-blur-sm" />
            <PrivacyConsentBanner
              onAcceptAll={handleAcceptAll}
              onRejectNonEssential={handleRejectNonEssential}
              onSaveCustom={handleSaveCustomConsent}
            />
          </>
        )}

        {/* Auth Modal */}
        <Modal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} size="auth">
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            initialView={authView}
          />
        </Modal>
      </div>
    </>
  );
}
